import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  jsonLd?: object;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  jsonLd
}) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (description) {
      updateMetaTag('description', description);
    }
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }
    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }
    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }
    if (ogUrl) {
      updateMetaTag('og:url', ogUrl, true);
    }

    // Update canonical link
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }

    // Add JSON-LD structured data
    if (jsonLd) {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, jsonLd]);

  return null;
};

// Helper function to generate organization schema
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "3DP Makina",
  "url": import.meta.env.VITE_SITE_URL || "https://example.com",
  "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
  "description": "CNC torna, dik işleme, freze, kaynak ve 3D prototipleme hizmetleri. Hassas imalatta güvenilir çözüm ortağınız.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1459. Cad, İvedik OSB, Özanadolu Sanayi Sitesi No:3",
    "addressLocality": "Yenimahalle",
    "addressRegion": "Ankara",
    "postalCode": "06480",
    "addressCountry": "TR"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+90-312-394-56-35",
    "contactType": "customer service",
    "email": "info@3dpmakina.com"
  },
  "sameAs": [
    "https://www.linkedin.com/company/3dpmakina",
    "https://www.facebook.com/3dpmakina"
  ]
});

// Helper function to generate local business schema
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "3DP Makina",
  "image": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
  "url": import.meta.env.VITE_SITE_URL || "https://example.com",
  "telephone": "+90-312-394-56-35",
  "email": "info@3dpmakina.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1459. Cad, İvedik OSB, Özanadolu Sanayi Sitesi No:3",
    "addressLocality": "Yenimahalle",
    "addressRegion": "Ankara",
    "postalCode": "06480",
    "addressCountry": "TR"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 39.9,
    "longitude": 32.7
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "08:00",
      "closes": "14:00"
    }
  ],
  "priceRange": "$$",
  "description": "CNC torna, dik işleme, freze, kaynak ve 3D prototipleme hizmetleri. Hassas imalatta güvenilir çözüm ortağınız."
});

// Helper function to generate service schema
export const getServiceSchema = (serviceName: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": serviceName,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "3DP Makina",
    "url": import.meta.env.VITE_SITE_URL || "https://example.com"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Turkey"
  },
  "serviceType": "Manufacturing"
});

// Helper function to generate article schema for blog posts
export const getArticleSchema = (title: string, description: string, author: string, publishDate: string, slug: string) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "author": {
    "@type": "Person",
    "name": author
  },
  "publisher": {
    "@type": "Organization",
    "name": "3DP Makina",
    "logo": {
      "@type": "ImageObject",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
    }
  },
  "datePublished": publishDate,
  "dateModified": publishDate,
  "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog/${slug}`,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog/${slug}`
  }
});
